"use strict";
/**
 * Collections canonical definition — the single source of truth for the `collections`
 * OpenSearch index family (owned/registered by the setup lambda).
 *
 * Per the canonical-shared-types model, `shared-types` (the pure, AWS-SDK-free contract
 * package) holds, per index family, BOTH:
 *   (a) the field-name constants (`COLLECTION_FIELDS`) — runtime values typed against
 *       `CollectionSearchDocument` keys so the contract and the constants cannot drift,
 *       and
 *   (b) the OpenSearch template body (`buildCollectionsTemplate`) — settings (INCLUDING
 *       the `analysis` analyzers/normalizers), `dynamic: true`, and explicit `properties`.
 *
 * Every other surface (the indexer writer, the search reader) references these instead of
 * authoring its own copy, so collection field names have exactly one source.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.COLLECTION_FIELDS = void 0;
exports.buildCollectionsTemplate = buildCollectionsTemplate;
/**
 * Canonical field-name constants for the `collections` index family.
 *
 * Each value is a field name constrained to be a real key of `CollectionSearchDocument`
 * via `satisfies Record<string, keyof CollectionSearchDocument>`, so a typo or a rename
 * that breaks the contract fails to compile. Covers every field the setup-lambda
 * `collections` template maps.
 */
exports.COLLECTION_FIELDS = {
    collection_id: 'collection_id',
    owner_identity_id: 'owner_identity_id',
    owner_screen_name: 'owner_screen_name',
    title: 'title',
    description: 'description',
    collection_tags: 'collection_tags',
    category: 'category',
    pattern_count: 'pattern_count',
    follower_count: 'follower_count',
    is_public: 'is_public',
    created_at: 'created_at',
    updated_at: 'updated_at',
};
/**
 * Build the composable OpenSearch index-template body for the `collections` family.
 *
 * Ported from the setup lambda's inline `collections` IndexDefinition (collection_id,
 * owner_identity_id, owner_screen_name, title, description, collection_tags, category,
 * pattern_count, follower_count, is_public, created_at, updated_at), with the `fpw_text`
 * analyzer applied to the free-text fields (`owner_screen_name`, `title`, `description`).
 * The full `settings.analysis` block (the `fpw_text` analyzer, the `english_*` filters,
 * and the `lowercase` normalizer) is copied from `materials-public-definition.ts` so the
 * analyzers travel with the mappings. `mappings.dynamic` is the literal `true` (the
 * BF-018 guard). Every property key is a member of `COLLECTION_FIELDS`, so the mapping
 * types exactly the fields the constants name.
 */
function buildCollectionsTemplate(opts) {
    return {
        index_patterns: [opts.pattern],
        template: {
            settings: {
                number_of_shards: opts.shards ?? 1,
                number_of_replicas: opts.replicas ?? 0,
                refresh_interval: opts.refresh ?? '5s',
                analysis: {
                    analyzer: {
                        fpw_text: {
                            type: 'custom',
                            tokenizer: 'standard',
                            filter: ['lowercase', 'asciifolding', 'english_possessive_stemmer', 'english_stop', 'english_stemmer'],
                        },
                    },
                    filter: {
                        english_stop: { type: 'stop', stopwords: '_english_' },
                        english_stemmer: { type: 'stemmer', language: 'english' },
                        english_possessive_stemmer: { type: 'stemmer', language: 'possessive_english' },
                    },
                    normalizer: {
                        lowercase: { type: 'custom', char_filter: [], filter: ['lowercase', 'asciifolding'] },
                    },
                },
            },
            mappings: {
                dynamic: true,
                properties: {
                    [exports.COLLECTION_FIELDS.collection_id]: { type: 'keyword' },
                    [exports.COLLECTION_FIELDS.owner_identity_id]: { type: 'keyword' },
                    [exports.COLLECTION_FIELDS.owner_screen_name]: { type: 'text', analyzer: 'fpw_text' },
                    [exports.COLLECTION_FIELDS.title]: { type: 'text', analyzer: 'fpw_text' },
                    [exports.COLLECTION_FIELDS.description]: { type: 'text', analyzer: 'fpw_text' },
                    [exports.COLLECTION_FIELDS.collection_tags]: { type: 'keyword', normalizer: 'lowercase' },
                    [exports.COLLECTION_FIELDS.category]: { type: 'keyword', normalizer: 'lowercase' },
                    [exports.COLLECTION_FIELDS.pattern_count]: { type: 'integer' },
                    [exports.COLLECTION_FIELDS.follower_count]: { type: 'integer' },
                    [exports.COLLECTION_FIELDS.is_public]: { type: 'boolean' },
                    [exports.COLLECTION_FIELDS.created_at]: { type: 'date' },
                    [exports.COLLECTION_FIELDS.updated_at]: { type: 'date' },
                },
            },
        },
        priority: 100,
        _meta: { managed_by: 'fpw', purpose: 'collections' },
    };
}
